import {Injectable} from '@angular/core';
import {ActivatedRoute, Router, UrlTree} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';

import {AppSettingsService, SubdomainEnum} from './app-settings.service';
import {StorageService} from './storage.service';
import HoldingDiscriminator from '../shared/enums/HoldingDiscriminator.enum';
import {
  HoldingUnderManagementTabs,
  FundUnderManagementTabs,
} from '../shared/types/GpTypes';
import {CookieService} from 'ngx-cookie-service';
import {TerraUtils} from '../shared/TerraUtils';
import {ClientDetailsResponse} from '../account/my-account/model/agentDetailsResponse.model';
import SectionsAccess from '../permission/enums/sectionsAccess.enum';

@Injectable({
  providedIn: 'root',
})
export class RoutingService {
  readonly originFundParamName = 'origin';

  constructor(
    private router: Router,
    private dialogRef: MatDialog,
    private appSettings: AppSettingsService,
    private storageService: StorageService,
    private cookieService: CookieService,
    private activatedRoute: ActivatedRoute
  ) {
    /*
    // Close any opened dialog when route changes
    // router.events.pipe(
    //   filter((event: RouterEvent) => event instanceof NavigationStart),
    //   tap(() => {
    //     this.dialogRef.closeAll();
    //   }
    //   )
    // ).subscribe();
    */
  }

  // There is an angular material open issue:
  // When redirecting when a dialog is opened, the class that prevents scrolling is not being removed
  // (as well as some other styles).
  // here we make sure they are removed when token expires and the user is redirected to the login page,
  // so that after being redirected back to the returnUrl, he will be able to scroll the page.
  removeDialogClassAndStyleFromHtml() {
    let classes = document.querySelector('html').getAttribute('class');
    classes = classes ? classes.replace('cdk-global-scrollblock', '') : '';
    document.querySelector('html').setAttribute('class', classes);

    let styles = document.querySelector('html').getAttribute('style');
    styles = styles ? styles.replace('left: 0px; top: 0px;', '') : '';
    document.querySelector('html').setAttribute('style', styles);
  }

  closeAllDialogs() {
    this.dialogRef.closeAll();
    this.removeDialogClassAndStyleFromHtml();
  }

  // getReferrerFromCookie():string{
  //   return this.cookieService.get(this.appSettings.referrerCookieKey);
  // }

  // setReferrerToCookie(referrerData:string){

  //   const date = new Date();

  //   date.setDate(date.getFullYear() + 1);

  //   this.cookieService.set(this.appSettings.referrerCookieKey, referrerData,date,'/', document.domain);
  // }

  navigateToLoginNoQuery() {
    this.dialogRef.closeAll();
    sessionStorage.clear();
    this.router.navigateByUrl(this.loginUrlBySubdomain);
    this.removeDialogClassAndStyleFromHtml();
  }

  navigateToLoginWithQuery(forceReload: boolean = false) {
    this.dialogRef.closeAll();
    sessionStorage.clear();
    this.removeDialogClassAndStyleFromHtml();
    let returnUrl = location.pathname + location.search;

    const isNotRegistered =
      !!this.activatedRoute.snapshot.queryParamMap.get('isRegistered') &&
      this.activatedRoute.snapshot.queryParamMap.get('isRegistered') ===
      'False';

    if (returnUrl === '/' || returnUrl.toLowerCase() === this.maintenancePage) {
      returnUrl = '';
    }

    this.setReturnUrlInCookie(returnUrl);

    // force reload the page if the current URL is not the login page
    // forceReload added in order to solve a bug where the user got stuck on login page with blank screen after failed token refresh
    if (
      (window.location.pathname !== this.loginUrlBySubdomain || forceReload) &&
      !isNotRegistered
    ) {
      window.location.href = this.loginUrlBySubdomain + location.search;
    } else if (
      (window.location.pathname !== this.loginUrlBySubdomain || forceReload) &&
      isNotRegistered
    ) {
      window.location.href = this.signupUrlBySubdomain + location.search;
    }
    /*    this.router.navigateByUrl(
          this.router.createUrlTree([this.loginUrlBySubdomain])
        );*/
  }

  getSignupUrlTree(
    clearQueryStringAndReturnURL = true,
    clearSessionStorage = true
  ): UrlTree {
    if (clearSessionStorage) {
      this.storageService.clear();
    }
    this.dialogRef.closeAll();

    let returnUrl = location.pathname + location.search;
    if (
      clearQueryStringAndReturnURL ||
      returnUrl === '/' ||
      returnUrl.toLowerCase() === this.maintenancePage
    ) {
      returnUrl = '';
    }
    const queryParams: any = {};

    const emailStartIndex = location.search.indexOf('email=');
    if (emailStartIndex > 0) {
      const emailEndIndex = location.search.indexOf('&', emailStartIndex);
      queryParams.email = location.search.substring(
        emailStartIndex + 6,
        emailEndIndex > 0 ? emailEndIndex : location.search.length
      );
    }

    const gpReferralKeyStartIndex = location.search.indexOf('gpref=');
    if (gpReferralKeyStartIndex > 0) {
      const gpReferralKeyEndIndex = location.search.indexOf(
        '&',
        gpReferralKeyStartIndex
      );
      queryParams.gpref = decodeURIComponent(
        location.search.substring(
          gpReferralKeyStartIndex + 6,
          gpReferralKeyEndIndex > 0
            ? gpReferralKeyEndIndex
            : location.search.length
        )
      );
    }

    this.setReturnUrlInCookie(returnUrl);

    return this.router.createUrlTree([this.signupUrlBySubdomain], {
      queryParams,
    });
  }

  getLoginUrlTree(
    clearQueryStringAndReturnURL = true,
    clearSessionStorage = true
  ): UrlTree {
    if (clearSessionStorage) {
      this.storageService.clear();
    }
    this.dialogRef.closeAll();

    let returnUrl = location.pathname + location.search;

    if (
      clearQueryStringAndReturnURL ||
      returnUrl === '/' ||
      returnUrl.toLowerCase() === this.maintenancePage
    ) {
      returnUrl = '';
    }
    const queryParams: any = {};

    const emailStartIndex = location.search.indexOf('email=');
    if (emailStartIndex > 0) {
      const emailEndIndex = location.search.indexOf('&', emailStartIndex);
      queryParams.email = location.search.substring(
        emailStartIndex + 6,
        emailEndIndex > 0 ? emailEndIndex : location.search.length
      );
    }

    const gpReferralKeyStartIndex = location.search.indexOf('gpref=');
    if (gpReferralKeyStartIndex > 0) {
      const gpReferralKeyEndIndex = location.search.indexOf(
        '&',
        gpReferralKeyStartIndex
      );
      queryParams.gpref = decodeURIComponent(
        location.search.substring(
          gpReferralKeyStartIndex + 6,
          gpReferralKeyEndIndex > 0
            ? gpReferralKeyEndIndex
            : location.search.length
        )
      );
    }
    this.setReturnUrlInCookie(returnUrl);

    return this.router.createUrlTree([this.loginUrlBySubdomain], {
      queryParams,
    });
  }

  serializeQueryParams(obj: any) {
    const str = [];
    for (const p in obj) {
      if (obj.hasOwnProperty(p)) {
        str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
      }
    }
    return str.join('&');
  }

  get maintenancePage() {
    return '/maintenance';
  }

  get loginUrlBySubdomain() {
    const isGp = this.appSettings.subDomain === SubdomainEnum.GP;
    return isGp ? this.loginGpPage : this.loginLpPage;
  }

  get signupUrlBySubdomain() {
    const isGp = this.appSettings.subDomain === SubdomainEnum.GP;
    return isGp ? this.signupGpPage : this.signupLpPage;
  }

  get defaultLoggedInPageBySubdomain() {
    const isGp = this.appSettings.subDomain === SubdomainEnum.GP;
    return isGp ? this.defaultGpPage : this.lpHomePage;
  }

  get loginGpPage() {
    return `/account/login`;
  }

  get signupGpPage() {
    return `/account/signup`;
  }

  get loginLpPage() {
    return `/lp/login`;
  }

  get signupLpPage() {
    return `/lp/signup`;
  }

  get gpOnboardingPage() {
    return `/onboarding`;
  }

  get lpOnboardingPage() {
    return `/lp/onboarding`;
  }

  get lpOnboardingPageDefault() {
    return `/lp/onboarding/personal`;
  }

  get lpOnboardingPagePersonalDetails() {
    return this.lpOnboardingPage + `/personal`;
  }

  get lpOnboardingPageOrganizationDetails() {
    return this.lpOnboardingPage + `/organization`;
  }

  get gpAssetsPage() {
    return `/dashboard/assets`;
  }

  get gpFundsPage() {
    return '/dashboard/funds';
  }

  get lpHoldingsPage() {
    return `/lp/portfolio`;
  }

  get lpHomePage() {
    return this.lpHoldingsPage;
  }

  /** Page with a list of the holdings and their bank account (can be clicked and edited) */
  get lpBankAccounts() {
    return `/lp/bank-accounts`;
  }

  get lpDocuments() {
    return `/lp/documents`;
  }

  lpDocument(id) {
    return `/lp/documents/${id}`;
  }

  lpEditBankAccount(bankAccountId) {
    return `${this.lpBankAccounts}/${bankAccountId}/edit`;
  }

  get lpCreateBankAccount() {
    return `${this.lpBankAccounts}/create`;
  }

  get lpCreateUnitApplication() {
    return `${this.lpBankAccounts}/unit/application/create`;
  }

  lpEditUnitApplication(applicationId: number) {
    return `${this.lpBankAccounts}/unit/application/${applicationId}/edit`;
  }

  lpUnitApplication(applicationId: number) {
    return `${this.lpBankAccounts}/unit/application/${applicationId}`;
  }

  lpUnitBankAccount(accountId: number) {
    return `${this.lpBankAccounts}/unit/bank-account/${accountId}`;
  }

  lpCreateUnitPayment() {
    return `${this.lpBankAccounts}/unit/payment/create`;
  }

  lpUnitPaymentPage(paymentId: number) {
    return `${this.lpBankAccounts}/unit/payment/${paymentId}`;
  }

  lpUnitPaymentReviewPage(paymentId: number) {
    return `${this.lpBankAccounts}/unit/payment/${paymentId}/review`;
  }

  lpUnitPaymentConfirmationPage(paymentId: number) {
    return `${this.lpBankAccounts}/unit/payment/${paymentId}/confirmation`;
  }

  get insightsPage() {
    return `/dashboard/insights`;
  }

  get creBankAccounts() {
    return `/dashboard/bank-accounts/cre`;
  }

  contactsPage() {
    return `/dashboard/contacts`;
  }

  positionsPage() {
    return `/dashboard/positions`;
  }

  bankAccountsPage(
    tab: 'cre' | 'gp' | 'investors' | 'applications' | 'cre_accounts' | '' = ''
  ) {
    return `/dashboard/bank-accounts/${tab}`;
  }

  createBankApplicationPage() {
    return `/dashboard/bank-accounts/applications/create`;
  }

  createCrePayment() {
    return `/dashboard/bank-accounts/cre/payment/create`;
  }

  paymentPage(paymentId: number) {
    return `/dashboard/bank-accounts/cre/payment/${paymentId}`;
  }

  paymentReviewPage(paymentId: number) {
    return `/dashboard/bank-accounts/cre/payment/${paymentId}/review`;
  }

  paymentConfirmationPage(paymentId: number) {
    return `/dashboard/bank-accounts/cre/payment/${paymentId}/confirmation`;
  }

  bankApplicationPage(applicationId: number, holdingId: number = null) {
    const qParams = `${holdingId ? '?holdingId=' + holdingId : ''}`;
    return `/dashboard/bank-accounts/applications/${applicationId}${qParams}`;
  }

  bankApplicationDraftEditPage(
    applicationId: number,
    holdingId: number = null
  ) {
    const qParams = `${holdingId ? '?holdingId=' + holdingId : ''}`;
    return `/dashboard/bank-accounts/applications/${applicationId}/edit${qParams}`;
  }

  bankApplicationContinueCreatePage(applicationId: number) {
    return `/dashboard/bank-accounts/applications/${applicationId}/continue-create`;
  }

  createCreBankPage() {
    return `/dashboard/bank-accounts/cre/create`;
  }

  creBankAccountPage(id: number) {
    return `/dashboard/bank-accounts/cre/${id}`;
  }

  externalBankAccountPage(id: number) {
    return `/dashboard/bank-accounts/external/${id}`;
  }

  gpAssetPage(assetId: number) {
    return `/dashboard/assets/${assetId}`;
  }

  gpFundPage(fundId: number) {
    return `/dashboard/funds/${fundId}`;
  }

  createAsset() {
    return `${this.gpAssetsPage}/create`;
  }

  createDraftAsset() {
    return `${this.gpAssetsPage}/create-draft`;
  }

  editDraftAsset(assetId: number) {
    return `${this.gpAssetsPage}/${assetId}/edit`;
  }

  editDraftFund(fundId: number) {
    return `${this.gpFundsPage}/${fundId}/edit`;
  }

  createFund() {
    return `${this.gpFundsPage}/create`;
  }

  createFundAsset(fundId: number) {
    return `${this.gpFundPage(fundId)}/portfolio/create`;
  }

  createAssetInitialFundraising(assetId: number) {
    return `${this.gpAssetPage(assetId)}/start-fundraising`;
  }

  assetFundraising(assetId: number, fundraisingId: number) {
    return `${this.gpAssetPage(assetId)}/contributions/${fundraisingId}`;
  }

  assetCapitalCall(assetId: number, fundraisingId: number) {
    return `${this.assetFundraising(assetId, fundraisingId)}/capital-call`;
  }

  fundFundraising(fundId: number, fundraisingId: number) {
    return `${this.gpFundPage(fundId)}/contributions/${fundraisingId}`;
  }

  fundCapitalCall(fundId: number, fundraisingId: number) {
    return `${this.fundFundraising(fundId, fundraisingId)}/capital-call`;
  }

  createAssetFundraising(assetId: number) {
    return `${this.gpAssetPage(assetId)}/contributions/create`;
  }

  createFundFundraising(fundId: number) {
    return `${this.gpFundPage(fundId)}/contributions/create`;
  }

  gpAssetWithTab(
    assetId: number,
    tab: HoldingUnderManagementTabs = 'overview'
  ) {
    return `${this.gpAssetPage(assetId)}/${tab}`;
  }

  gpFundWithTab(fundId: number, tab: FundUnderManagementTabs = 'overview') {
    return `${this.gpFundPage(fundId)}/${tab}`;
  }

  createAssetReport(assetId: number) {
    return `${this.gpAssetPage(assetId)}/reports/create`;
  }

  createFundReport(fundId: number) {
    return `${this.gpFundPage(fundId)}/reports/create`;
  }

  gpAssetReport(assetId: number, reportId: number) {
    return `${this.gpAssetPage(assetId)}/reports/${reportId}`;
  }

  gpFundReport(fundId: number, reportId: number) {
    return `${this.gpFundPage(fundId)}/reports/${reportId}`;
  }

  assetReportFaqs(assetId: number, reportId: number) {
    return `${this.gpAssetPage(assetId)}/reports/${reportId}/faqs`;
  }

  fundReportFaqs(fundId: number, reportId: number) {
    return `${this.gpFundPage(fundId)}/reports/${reportId}/faqs`;
  }

  assetDistribution(assetId: number, distributionId: number) {
    return `${this.gpAssetPage(assetId)}/distributions/${distributionId}`;
  }

  fundDistribution(fundId: number, distributionId: number) {
    return `${this.gpFundPage(fundId)}/distributions/${distributionId}`;
  }

  assetEditDistribution(assetId: number, distributionId: number) {
    return `${this.gpAssetPage(assetId)}/distributions/${distributionId}/edit`;
  }

  fundEditDistribution(fundId: number, distributionId: number) {
    return `${this.gpFundPage(fundId)}/distributions/${distributionId}/edit`;
  }

  createAssetDistribution(assetId: number) {
    return `${this.gpAssetPage(assetId)}/distributions/create`;
  }

  createFundDistribution(fundId: number) {
    return `${this.gpFundPage(fundId)}/distributions/create`;
  }

  /** GP and LP account settings page */
  get myAccountPage() {
    const isGp = this.appSettings.subDomain === SubdomainEnum.GP;
    return isGp ? this.myAccountPageGp : this.myAccountPageLp;
  }

  /** GP account settings page */
  get myAccountPageGp() {
    return '/account/me';
  }

  /** LP account settings page */
  get myAccountPageLp() {
    return '/lp/profile';
  }

  get noAccess() {
    return '/no-access';
  }

  /** LP asset page, with a specific tab active */
  lpAsset(
    assetId: number,
    tab: 'information' | 'transactions' | 'documents' | 'capital-call' = null
  ) {
    return `${this.lpHoldingsPage}/a/${assetId}/${tab ? tab : ''}`;
  }

  /** LP fund page, with a specific tab active */
  lpFund(
    fundId: number,
    tab: 'information' | 'transactions' | 'documents' | 'capital-call' = null
  ) {
    return `${this.lpHoldingsPage}/f/${fundId}/${tab ? tab : ''}`;
  }

  lpOpportunities(opportunityId: string) {
    return `/lp/opportunity/${opportunityId}/campaign`;
  }

  lpOfferingDeck(opportunityId: string) {
    return `/lp/opportunity/${opportunityId}`;
  }

  lpOpportunityCampaign(opportunityId: string, holdingId: number) {
    return `${this.lpOpportunities(opportunityId)}/${holdingId}`;
  }

  /**  Preview page for a holding document (for LP) */
  storageObjectPreviewPage(
    holdingId: number,
    holdingDiscriminator: HoldingDiscriminator,
    storageObjectId: number
  ) {
    switch (holdingDiscriminator) {
      case HoldingDiscriminator.Asset:
        return `${this.lpAsset(holdingId, 'documents')}/${storageObjectId}`;
      case HoldingDiscriminator.Fund:
        return `${this.lpFund(holdingId, 'documents')}/${storageObjectId}`;
    }
  }

  /**  LP Holding (asset or fund) page with tab. The holding type should be provided. */
  lpHoldingPageWithTab(
    holdingId: number,
    holdingDiscriminator: HoldingDiscriminator,
    tab: 'information' | 'transactions' | 'documents' | 'capital-call' = null
  ) {
    switch (holdingDiscriminator) {
      case HoldingDiscriminator.Asset:
        return `${this.lpAsset(holdingId, tab)}`;
      case HoldingDiscriminator.Fund:
        return `${this.lpFund(holdingId, tab)}`;
    }
  }

  importAssetDistributions(assetId: number) {
    return `${this.gpAssetPage(assetId)}/distributions/import`;
  }

  importFundDistributions(fundId: number) {
    return `${this.gpFundPage(fundId)}/distributions/import`;
  }

  importFundContributions(holdingId: number) {
    return `${this.gpFundPage(holdingId)}/contributions/import`;
  }

  importAssetContributions(holdingId: number) {
    return `${this.gpAssetPage(holdingId)}/contributions/import`;
  }

  investorsManagement(holdingId: number, discriminator: HoldingDiscriminator) {
    return `${discriminator === HoldingDiscriminator.Asset ? this.gpAssetPage(holdingId) : this.gpFundPage(holdingId)}/investors-management`;
  }

  setReturnUrlInCookie(returnUrl: string) {
    if (!returnUrl || !this.isUrlToSetInCookie(returnUrl)) {
      return;
    }
    const expiresDate = new Date();
    expiresDate.setHours(expiresDate.getHours() + 1);
    this.cookieService.set(
      this.returnUrl,
      returnUrl,
      expiresDate,
      '/',
      document.domain
    );
  }

  readonly returnUrl: string = 'returnUrl';

  get getReturnUrlFromCookie() {
    const returnUrl = this.cookieService.get(this.returnUrl);
    if (!returnUrl) {
      return '';
    }
    return returnUrl;
  }

  removeReturnUrlFromCookie() {
    this.cookieService.delete(this.returnUrl, '/', document.domain);
  }

  goToReturnUrl(defaultUrl: string = '') {
    const returnUrl = this.getReturnUrlFromCookie;
    if (!!returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.router.navigateByUrl(defaultUrl);
    }
  }

  get defaultGpPage(): string {
    const clientDetails =
      this.storageService.getCredentialsData() as ClientDetailsResponse;
    if (!clientDetails || !clientDetails.userPermission) {
      return this.gpAssetsPage;
    }

    const userAllowedSections = clientDetails.userPermission.allowedSections;
    if (
      SectionsAccess.isOn(userAllowedSections, SectionsAccess.All) ||
      SectionsAccess.isOn(userAllowedSections, SectionsAccess.Assets)
    ) {
      return this.gpAssetsPage;
    } else if (SectionsAccess.isOn(userAllowedSections, SectionsAccess.Funds)) {
      return this.gpFundsPage;
    } else if (
      SectionsAccess.isOn(userAllowedSections, SectionsAccess.Contacts)
    ) {
      return this.contactsPage();
    } else if (
      SectionsAccess.isOn(userAllowedSections, SectionsAccess.Positions)
    ) {
      return this.positionsPage();
    } else if (
      SectionsAccess.isOn(userAllowedSections, SectionsAccess.BankAccounts)
    ) {
      return this.bankAccountsPage();
    } else if (
      SectionsAccess.isOn(userAllowedSections, SectionsAccess.Insights)
    ) {
      return this.insightsPage;
    } else if (SectionsAccess.isOn(userAllowedSections, SectionsAccess.None)) {
      return this.myAccountPageGp;
    }
  }

  private isUrlToSetInCookie(returnUrl: string): boolean {
    if (returnUrl.indexOf(TerraUtils.consts.paths.EMAIL_CONFIRM) !== -1) {
      return false;
    }
    if (returnUrl.indexOf(TerraUtils.consts.paths.NOT_FOUND) !== -1) {
      return false;
    }
    if (
      returnUrl.indexOf(TerraUtils.consts.paths.TWO_FACTOR_VERIFICATION) !== -1
    ) {
      return false;
    }
    return true;
  }

  gpSignAgreementUrl(investmentId: number, identifier: string) {
    return `sign-request/${investmentId}/${identifier}/1`;
  }
}
